import {useEffect, useMemo, useRef, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Select, Spin} from 'antd';
import {GET_LISTED_INSTRUMENTS} from '../resolvers';
import {isGqlError} from '../constants';
import styles from './workspace.module.scss';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Flattens a PublicEquity instrument into the fields the pick forms display.
 *
 * Both the picker (`adminGetStockExchangeListedInstruments`) and a pick's own
 * `instrument` return the flat PublicEquity shape — name/icon/currency sit
 * directly on the instrument, not under `baseFinancialInstrumentType`.
 */
export const instrumentSummary = instrument => {
  if (!instrument) return null;
  return {
    id: instrument.id,
    ticker: instrument.ticker,
    name: instrument.name,
    icon: instrument.icon,
    lastPrice: instrument.latestQuote?.lastPrice,
    priceAsOf: instrument.latestQuote?.asOf,
    currencyCode:
      instrument.latestQuote?.baseCurrencyCode ||
      instrument.currency?.currencyCode,
  };
};

/**
 * PublicEquity picker, scoped to the parent recommendation's exchange.
 *
 * The value is the PublicEquity `id` — never a Company id and never a ticker.
 * `seed` keeps an already-selected instrument visible even when it is not on
 * the first page of results.
 */
export const InstrumentSelect = ({
  exchangeCode,
  value,
  onChange,
  onSelectInstrument,
  seed,
  disabled,
  placeholder = 'Search by name or ticker',
}) => {
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(1);
  const [instruments, setInstruments] = useState([]);
  const debounceRef = useRef();

  useEffect(() => {
    // A parent exchange change invalidates every previously loaded instrument.
    setInstruments([]);
    setPage(1);
    setTerm('');
  }, [exchangeCode]);

  const {data, loading} = useQuery(GET_LISTED_INSTRUMENTS, {
    variables: {
      exchangeCode,
      searchTerm: term || null,
      page,
      limit: PAGE_SIZE,
    },
    skip: !exchangeCode,
    errorPolicy: 'all',
  });

  const result = data?.adminGetStockExchangeListedInstruments;
  const pageResult = isGqlError(result) ? null : result;

  useEffect(() => {
    if (!pageResult?.instruments) return;
    setInstruments(previous => {
      const base = pageResult.page === 1 ? [] : previous;
      const merged = [...base];
      pageResult.instruments.forEach(instrument => {
        if (!merged.some(existing => existing.id === instrument.id))
          merged.push(instrument);
      });
      return merged;
    });
  }, [pageResult]);

  const options = useMemo(() => {
    const summaries = instruments.map(instrumentSummary);
    if (seed?.id && !summaries.some(item => item.id === seed.id))
      summaries.unshift(seed);
    return summaries.map(item => ({
      value: item.id,
      label: `${item.ticker ? `${item.ticker} · ` : ''}${item.name || item.id}`,
      instrument: item,
    }));
  }, [instruments, seed]);

  const handleSearch = nextTerm => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setTerm(nextTerm);
    }, SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  return (
    <Select
      showSearch
      allowClear
      disabled={disabled || !exchangeCode}
      value={value}
      placeholder={placeholder}
      filterOption={false}
      onSearch={handleSearch}
      onChange={(nextValue, option) => {
        onChange?.(nextValue);
        onSelectInstrument?.(option?.instrument || null);
      }}
      onPopupScroll={event => {
        const {target} = event;
        const nearBottom =
          target.scrollTop + target.offsetHeight >= target.scrollHeight - 24;
        if (nearBottom && pageResult?.hasNextPage && !loading)
          setPage(current => current + 1);
      }}
      notFoundContent={loading ? <Spin size="small" /> : 'No instruments found'}
      optionLabelProp="label"
      options={options}
      loading={loading}
      className={styles.fullWidth}
    />
  );
};

export default InstrumentSelect;
